/**
 * Phase 13 Beat 3 — Candidate Applications table (Executive side).
 * Shows the full application history for a candidate. Score displayed here is
 * the FROZEN match snapshot at submission time (rule from spec).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  Briefcase, Buildings, MapPin, ArrowsClockwise, CheckCircle, Clock, XCircle,
  Link as LinkIcon, User, FileText,
} from "@phosphor-icons/react";

const STATUS_META = {
  started: { icon: Clock, tone: "bg-amber-50 text-amber-800 border-amber-200", label: "Started" },
  submitted: { icon: CheckCircle, tone: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Submitted" },
  cancelled: { icon: XCircle, tone: "bg-slate-100 text-slate-600 border-slate-200", label: "Not submitted" },
};

const fmt = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return d; }
};

export default function CandidateApplicationsPanel({ candidateId, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.get(`/candidates/${candidateId}/applications`);
      setRows(data.rows || []);
    } catch (ex) {
      // Feb 2026 — surface the actual HTTP status + upstream detail so the
      // UI shows a real error state instead of silently falling into the
      // "No applications yet" empty state. Keep the previous `rows` in place
      // so a transient blip doesn't wipe the screen; the error banner is the
      // authoritative signal (empty-state render is suppressed while `error`
      // is truthy).
      const status = ex.response?.status;
      const detail = formatApiError(ex.response?.data?.detail);
      const statusText = ex.response?.statusText;
      const parts = [];
      if (status) parts.push(`HTTP ${status}${statusText ? ` ${statusText}` : ""}`);
      parts.push(detail || ex.message || "Failed to load applications");
      setError(parts.join(" — "));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidateId, refreshKey]);

  const viewJD = async (aid) => {
    try {
      const { data } = await api.get(`/applications/${aid}/jd`);
      const win = window.open("", "_blank", "noopener,noreferrer");
      if (win) {
        // XSS-safe: use DOM APIs and textContent so JD is rendered as plain
        // text and never parsed as HTML (drops the need for manual escaping).
        win.document.title = `JD snapshot · ${new Date(data.snapshot.captured_at).toLocaleString()}`;
        const pre = win.document.createElement("pre");
        Object.assign(pre.style, {
          whiteSpace: "pre-wrap", fontFamily: "sans-serif",
          padding: "24px", maxWidth: "800px", margin: "auto",
        });
        pre.textContent = data.snapshot.jd_text || "";
        win.document.body.appendChild(pre);
      }
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "JD snapshot unavailable");
    }
  };

  return (
    <div className="space-y-3" data-testid="candidate-applications-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
            <Briefcase size={18} /> Applications
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Historical record — score shown is the match at submission time.
          </p>
        </div>
        <button onClick={load} disabled={busy}
                 data-testid="candidate-applications-refresh"
                 className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 rounded p-3 text-sm text-red-800 flex items-start gap-3"
             data-testid="candidate-applications-error">
          <XCircle size={16} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">Couldn't load applications</div>
            <div className="text-xs mt-0.5 break-words">{error}</div>
          </div>
          <button onClick={load} disabled={busy}
                   data-testid="candidate-applications-retry"
                   className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded border border-red-300 bg-white text-red-700 hover:bg-red-100 disabled:opacity-50 shrink-0">
            <ArrowsClockwise size={11} /> Retry
          </button>
        </div>
      )}

      {!busy && !error && rows.length === 0 && (
        <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500"
             data-testid="candidate-applications-empty">
          No applications yet. Use the Recommended Jobs tab to apply on this candidate's behalf.
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="text-left px-3 py-2">Job</th>
                <th className="text-left px-3 py-2">Match</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Submitted</th>
                <th className="text-left px-3 py-2">Submitted By</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((a) => {
                const meta = STATUS_META[a.status] || STATUS_META.started;
                const Icon = meta.icon;
                const snap = a.match_snapshot || {};
                return (
                  <tr key={a.id} data-testid={`application-row-${a.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{a.job_title || "—"}</div>
                      <div className="text-xs text-slate-500 inline-flex items-center gap-2">
                        <Buildings size={10} /> {a.company || "—"}
                        <MapPin size={10} /> {a.location || "—"}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-slate-900 font-semibold" data-testid={`application-score-${a.id}`}>
                        {snap.overall_score != null ? `${snap.overall_score}%` : "—"}
                      </div>
                      <div className="text-[11px] text-slate-500">{snap.band}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${meta.tone} inline-flex items-center gap-1`}
                            data-testid={`application-status-${a.id}`}>
                        <Icon size={10} weight="fill" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600" data-testid={`application-date-${a.id}`}>
                      {fmt(a.submitted_at || a.started_at)}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 inline-flex items-center gap-1"
                        data-testid={`application-submitted-by-${a.id}`}>
                      <User size={10} /> {a.submitted_by_name || "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => viewJD(a.id)}
                               data-testid={`application-view-jd-${a.id}`}
                               className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50">
                        <FileText size={11} /> View JD
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
